export interface ReleaseFeatureItem {
  title: string;
  description: string;
  beta?: boolean;
  /** For deprecated items: what replaces this feature. */
  replacedBy?: string;
  /** Path under /public to a screenshot of the feature. */
  image?: string;
  /** width/height of `image`, used to reserve layout space before it loads. */
  imageAspect?: number;
}

export interface ReleaseFeatureGroup {
  /** Optional sub-heading within a New/Deprecated section (e.g. "Agents", "Delivery"). */
  label?: string;
  items: ReleaseFeatureItem[];
}

export interface ReleaseNote {
  version: string;
  date: string;
  eyebrow: string;
  title: string;
  titleEmphasis: string;
  lede: string;
  newFeatures: ReleaseFeatureGroup[];
  existingFeatures: ReleaseFeatureItem[];
  deprecatedFeatures: ReleaseFeatureGroup[];
}

export const releaseNotes: ReleaseNote[] = [
  {
    version: 'v2.4.0',
    date: 'July 4, 2026',
    eyebrow: 'The Independence Day release',
    title: 'Freedom from',
    titleEmphasis: 'busywork',
    lede: "Our July 4th release ships new features for exploring, querying, and securing your Salesforce orgs — and retires a few old ones. Here's everything that changed.",
    newFeatures: [
      {
        label: 'Agents',
        items: [
          {
            title: 'Agents',
            description:
              'A list of out-of-the-box agents built from our implementation experience — and you can add as many of your own agents as you need.',
            image: '/release-notes/agents.jpg',
            imageAspect: 1.8568,
          },
          {
            title: 'Weave',
            description:
              "Let's Weave — the centralized screen that lets you interact with anything and everything in nCoder. This is the heart of the application: users can do anything from here.",
            image: '/release-notes/weave.jpg',
            imageAspect: 1.8667,
          },
          {
            title: 'Migrations',
            description: 'Reusable automation templates that run against your connected org.',
            image: '/release-notes/migrations.jpg',
            imageAspect: 1.8568,
          },
          {
            title: 'Scheduling',
            description: 'Manage recurring scheduled jobs for the selected org.',
            image: '/release-notes/scheduling.jpg',
            imageAspect: 1.8543,
          },
        ],
      },
      {
        label: 'Delivery',
        items: [
          {
            title: 'Reports',
            description:
              'Review completed migration and agent runs, open saved outcomes, and download generated artifacts.',
            image: '/release-notes/reports.jpg',
            imageAspect: 1.8592,
          },
          {
            title: 'MCP',
            description: 'Live MCP tools, connector setup, and external service credentials in one place.',
            image: '/release-notes/mcp.jpg',
            imageAspect: 1.7789,
          },
          {
            title: 'Git and Pipelines',
            description:
              'Connect to your Git and perform Git operations from here. Check in and check out code, create branches — everything you can do from the Git CLI, without leaving nCoder.',
            image: '/release-notes/git-and-pipelines.jpg',
            imageAspect: 1.8519,
          },
          {
            title: 'ORG 360',
            description:
              'nCoder traverses every relationship in your org and builds a living knowledge graph — connecting objects, code, flows, security, and metadata into a single navigable map.',
            image: '/release-notes/org-360.jpg',
            imageAspect: 1.847,
          },
          {
            title: 'Salesforce Releases',
            description:
              'Upload any Salesforce release notes PDF. nCoder cross-references every change against your live org and tells you exactly what breaks, what needs review, and what improves — before it hits production.',
            image: '/release-notes/salesforce-releases.jpg',
            imageAspect: 1.7766,
          },
          {
            title: 'Deployment',
            description:
              'Deploy your code from here to Salesforce or GitHub. A one-stop solution — you never have to leave nCoder.',
            image: '/release-notes/deployment.jpg',
            imageAspect: 1.7588,
          },
          {
            title: 'Testing',
            description:
              'Describe tests in plain English — the agent generates a Playwright spec, you approve, then it runs against your sandbox.',
            beta: true,
            image: '/release-notes/testing.jpg',
            imageAspect: 1.8494,
          },
          {
            title: 'Observability',
            description: 'Agent, tool, and service health from the metrics tables.',
            image: '/release-notes/observability.jpg',
            imageAspect: 1.7544,
          },
          {
            title: 'Salesforce Debugger',
            description: 'Inspect governor usage, SOQL/DML pressure, call flow, failing components, and fix evidence.',
            image: '/release-notes/salesforce-debugger.jpg',
            imageAspect: 1.7522,
          },
        ],
      },
    ],
    existingFeatures: [
      {
        title: 'Home',
        description: 'The home screen that gives you a full picture of the application. Use the Dashboard.',
        image: '/release-notes/home.jpg',
        imageAspect: 1.7699,
      },
      {
        title: 'Metadata Explorer',
        description: "Shows all the metadata you've downloaded from your connected org. Use the Metadata Explorer.",
        image: '/release-notes/metadata-explorer.jpg',
        imageAspect: 1.7032,
      },
      {
        title: 'Documentation',
        description: 'Shows the documentation for all your org objects. Use the Documentation screen.',
        image: '/release-notes/documentation.jpg',
        imageAspect: 1.699,
      },
      {
        title: 'Text to SOQL',
        description:
          'Write queries in natural language, translate them into SOQL, and execute the statements. Use the Text to SOQL screen.',
        image: '/release-notes/text-to-soql.jpg',
        imageAspect: 1.697,
      },
      {
        title: 'Security Scanner',
        description: 'Shows the scan results across your entire org. Use the Security Scanner.',
        image: '/release-notes/security-scanner.jpg',
        imageAspect: 1.8642,
      },
      {
        title: 'Connection Manager',
        description:
          'Shows all your existing org connections and lets you create new connections to other orgs. Use the Connection Manager.',
        image: '/release-notes/connection-manager.jpg',
        imageAspect: 1.8717,
      },
    ],
    deprecatedFeatures: [
      {
        label: 'Delivery',
        items: [
          {
            title: 'Old Chat',
            description: 'The old chat screen is now deprecated and has been replaced with Weave.',
            replacedBy: 'Weave',
            image: '/release-notes/old-chat.jpg',
            imageAspect: 1.8568,
          },
        ],
      },
    ],
  },
];
